export const toUnderscore = (str: string): string => str.split(' ').join('_');
export const toArray = <T>(obj: Record<string, T>): { key: string; value: T }[] => Object.keys(obj).map((key) => ({ key, value: obj[key] }));

export const isNullable = (value: unknown): value is null | undefined => value === null || value === undefined;

export const isEmpty = (obj: Record<string, unknown> | null | undefined) => {
  if (isNullable(obj)) {
    return true;
  }
  return Object.keys(obj || {}).length === 0;
};

/**
 * Filters out null and undefined values from the arguments.
 * This is a performant way to conditionally pass arguments.
 */
export const passAsArgs = <T>(...args: Array<T | null | undefined>) => args.filter((arg): arg is T => !isNullable(arg));

export const formatPrice = (price: number | string) => `$${Number(price).toFixed(2)}`;

export const formatPoints = (points: number | string) => Number(points).toLocaleString('en-US');

export const upperCaseFirstLetter = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export function toCapitalizedCase(string: string) {
  // copied from 'capitalizeOnlyFirstWord' type checker
  if (typeof string !== 'string') {
    console.error('"toCapitalizedCase()" accepts only strings!');
    return '';
  }
  return string
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getUrlVars(url: string) {
  const vars: Record<string, string> = {};
  url.replace(/[?&]+([^=&]+)=([^&]*)/gi, (_match, key: string, value: string) => {
    vars[key] = decodeURIComponent(value);
  });
  return vars;
}

export function attributeNumToString(number: number) {
  return (
    {
      1: 'one',
      2: 'two',
      3: 'three',
      4: 'four',
    }[number] || 'zero'
  );
}

export function numOfAttributes(product: Product, hasColorSwatchesFlag: boolean, hasSizesFlag: boolean, hasTypesFlag: boolean) {
  let attributeCount = 0;

  if (hasColorSwatchesFlag) {
    attributeCount += 1;
  }
  if (hasSizesFlag) {
    attributeCount += 1;
    if (product.traits?.sizes?.hasWidth || product.traits?.sizes?.hasDressSplit) {
      attributeCount += 1;
    }
  }
  if (hasTypesFlag) {
    attributeCount += 1;
  }
  return attributeNumToString(attributeCount);
}

/**
 * generates random id like rdm-key-41-171353965872
 */
export const randomGenerator = () => `rdm-key-${Math.floor(Math.random() * 1000)}-${Date.now()}`;

/**
 * returns percentage number based on valueRange of 1-3(bcom) or 1-7(mcom)
 */
export const reviewsPercentage = (value: number, valueRange: number) => {
  const midpoint = Math.ceil(valueRange / 2);
  return value <= midpoint ? ((value - 1) / (midpoint - 1)) * 50 : 50 + ((value - midpoint) / (valueRange - midpoint)) * 50;
};

function buildQueryParamStringFromObject(params: Record<string, unknown> = {}) {
  const searchParams = new URLSearchParams();
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value != null && !(typeof value === 'string' && value.trim() === '')) {
      acc.append(key, String(value));
    }
    return acc;
  }, searchParams);
  return searchParams.toString();
}

const paramMap = {
  pdpServer: 'pdpserver',
  shoppingMode: '_shoppingMode',
  regionCode: '_regionCode',
  currencyCode: 'currencyCode',
  customerState: '_customerState',
  deviceType: '_deviceType',
};
export const getContextParams = (context: Record<string, unknown> | null | undefined = {}) => {
  if (!context) return {};

  return Object.fromEntries(
    Object.entries(paramMap)
      .filter(([key]) => context[key])
      .map(([key, value]) => [value, context[key]]),
  );
};
export function buildUrl(baseUrl = '', params: Record<string, unknown> = {}) {
  if (!(typeof baseUrl === 'string') || Object.prototype.toString.call(params) !== '[object Object]') {
    return '';
  }

  const queryParams = buildQueryParamStringFromObject(params);
  let joinSymbol = '';
  if (queryParams.length) {
    joinSymbol = baseUrl.indexOf('?') !== -1 ? '&' : '?';
  }

  return `${baseUrl}${joinSymbol}${queryParams}`;
}

/**
 * Maps an array of objects into an object keyed by a specific property.
 */
export function mapByKey<T extends Record<string, unknown>>(array: T[], key = 'id'): Record<string, T> {
  return array.reduce(
    (acc, item) => {
      acc[item[key]] = { ...item };
      return acc;
    },
    {} as Record<string, T>,
  );
}
